import { DEFAULT_PAGE_SIZE } from "@/core/constants/pagination";
import type { HttpQueryParam, HttpRequestConfig, IHttpClient } from "@/core/interfaces";
import { isAPIError } from "@/core/services";
import {
  extractDetail,
  extractItems,
  extractTotal,
  toPaginatedQuery,
  isRecord,
  readErrorDetails,
  readFirstText,
} from "@/core/utils";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import {
  appendExportColumns,
  appendReportQuery,
  parseContentDispositionFileName,
} from "@/modules/export";
import type { ExportedFile, ExportFormat } from "@/modules/export";
import type { ObrasColumnKey } from "./obras-lista.types";
import { hojeBrasilia } from "./hoje-brasilia";
import type { CriarObraPayload } from "./criar-obra.types";
import { OBRAS_EXPORT_COLUMN_KEYS } from "./obras.constants";
import { DEFAULT_OBRAS_SORT, EMPTY_OBRAS_FILTERS } from "./obras-lista.types";
import type {
  ObraListaItem,
  ObraTotalPorStatus,
  ObrasFilters,
  ObrasPage,
  ObrasSort,
} from "./obras-lista.types";
import type {
  CriarObraResultado,
  ObraEmbarcacaoInformacoes,
  ObraEmbarcacaoOpcao,
  ObraLimiteClasse,
  ObraLimiteClasseDetalhe,
  ObraLocalOpcao,
  ObraPeriodoOpcao,
  ObraStatusOpcao,
  PeriodoObraEtapa,
  ObraUltimaObra,
  CentroCustoOpcao,
  TipoObraOpcao,
} from "./obras.types";

/** Contrato consumido do hub-bff (HU 2519), privilégio `hub.obra:visualizar`; GET, exceto `CRIAR`. */
export const OBRAS_PATHS = {
  /** HU 2543 (Revisão e Criar Obra): `POST`, privilégio `hub.obra:criar`; `201 { id, codigo }`. */
  CRIAR: "/api/obras",
  /** HU 2521 (Visualizar Obras): `GET`, privilégio `hub.obra:visualizar`. */
  LISTAR: "/api/obras",
  /** HU 2531 (Exportar Visualização): `GET` binário, privilégio `hub.obra:visualizar`. */
  EXPORTAR: "/api/obras/exportacao",
  EMBARCACOES: "/api/obras/embarcacoes",
  LOCAIS: "/api/obras/locais",
  STATUS_OBRA: "/api/obras/status-obra",
  /** HU 2523 (etapa Duração), privilégio `hub.obra:visualizar`. */
  PERIODOS_OBRA: "/api/obras/periodos-obra",
  /** HU 2525 (etapa Tipos de Obra), privilégio `hub.obra:visualizar`. */
  TIPOS_OBRA: "/api/obras/tipos-obra",
  EMBARCACAO_CENTROS_CUSTO: (embarcacaoId: string) =>
    `/api/obras/embarcacoes/${encodeURIComponent(embarcacaoId)}/centros-custo`,
  EMBARCACAO_INFORMACOES: (embarcacaoId: string) =>
    `/api/obras/embarcacoes/${encodeURIComponent(embarcacaoId)}/informacoes`,
  /** HU 2549, privilégio `hub.obra:visualizar`. */
  EMBARCACAO_LIMITES_CLASSE: (embarcacaoId: string) =>
    `/api/obras/embarcacoes/${encodeURIComponent(embarcacaoId)}/limites-classe`,
} as const;

interface RequestOptions {
  signal?: AbortSignal;
}

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

function toEmbarcacaoOpcao(raw: unknown): ObraEmbarcacaoOpcao | null {
  if (!isRecord(raw)) return null;

  const id = readFirstText(raw, "id").trim();
  if (!id) return null;

  return { id, nome: readFirstText(raw, "nome").trim() || id };
}

function toLocalOpcao(raw: unknown): ObraLocalOpcao | null {
  if (!isRecord(raw)) return null;

  const id = readFirstText(raw, "id").trim();
  if (!id) return null;

  return {
    id,
    codigo: readFirstText(raw, "codigo").trim(),
    nome: readFirstText(raw, "nome").trim() || id,
  };
}

function toStatusOpcao(raw: unknown): ObraStatusOpcao | null {
  if (!isRecord(raw)) return null;

  const id = readFirstText(raw, "id").trim();
  if (!id) return null;

  return {
    id,
    codigo: readFirstText(raw, "codigo").trim(),
    descricao: readFirstText(raw, "descricao").trim() || id,
    // Conservador: sem a flag, a opção não é oferecida na criação (RN05).
    disponivelNaCriacao: raw.disponivelNaCriacao === true,
  };
}

const PERIODO_ETAPAS: readonly PeriodoObraEtapa[] = [
  "NAVEGACAO_ENTRADA",
  "OBRA",
  "TESTES",
  "ACEITACAO",
  "NAVEGACAO_SAIDA",
];

function toPeriodoOpcao(raw: unknown): ObraPeriodoOpcao | null {
  if (!isRecord(raw)) return null;

  const id = readFirstText(raw, "id").trim();
  if (!id) return null;

  const etapa = readFirstText(raw, "etapa").trim();

  return {
    id,
    descricao: readFirstText(raw, "descricao").trim() || id,
    etapa: PERIODO_ETAPAS.find((valor) => valor === etapa) ?? null,
  };
}

function toTipoObraOpcao(raw: unknown): TipoObraOpcao | null {
  if (!isRecord(raw)) return null;

  const id = readFirstText(raw, "id").trim();
  if (!id) return null;

  const codigo = readFirstText(raw, "codigo").trim();
  return {
    id,
    codigo,
    nome: readFirstText(raw, "nome").trim() || codigo || id,
    ativo: raw.ativo !== false,
  };
}

function toCentroCustoOpcao(raw: unknown): CentroCustoOpcao | null {
  if (!isRecord(raw)) return null;

  const id = readFirstText(raw, "id").trim();
  if (!id) return null;

  return {
    id,
    centroCusto: readFirstText(raw, "centroCusto", "centro_custo").trim() || id,
    descricao: readFirstText(raw, "descricao").trim(),
    subgrupo: readFirstText(raw, "subgrupo").trim(),
    // Conservador: sem a flag, o CC é tratado como bloqueado e não é oferecido (RN20).
    bloqueado: raw.bloqueado !== false,
  };
}

function toUltimaObra(raw: unknown): ObraUltimaObra | null {
  if (!isRecord(raw)) return null;

  const id = readFirstText(raw, "id", "obraId").trim();
  if (!id) return null;

  return {
    id,
    codigo: readFirstText(raw, "codigo").trim(),
    nome: readFirstText(raw, "nome", "descricao").trim(),
  };
}

function toLimiteClasse(raw: unknown): ObraLimiteClasse | null {
  if (!isRecord(raw)) return null;

  const nome = readFirstText(raw, "nome", "descricao", "tipo").trim();
  if (!nome) return null;

  // `limitesClasse` de `/informacoes` vem como `LimiteClasseDto` do hub-core ({ vistoria, descricao,
  // dataLimite }): a data é `dataLimite` e não há `vencido`, que se deriva em relação a hoje.
  const vencimento = readFirstText(raw, "vencimento", "dataLimite", "validade", "data").trim();

  return {
    nome,
    vencimento,
    vencido: raw.vencido === true || (vencimento !== "" && vencimento < hojeBrasilia()),
  };
}

function toLimiteClasseDetalhe(raw: unknown): ObraLimiteClasseDetalhe | null {
  if (!isRecord(raw)) return null;

  const vistoria = readFirstText(raw, "vistoria").trim();
  const dataLimite = readFirstText(raw, "dataLimite").trim();
  if (!vistoria || !dataLimite) return null;

  return {
    vistoria,
    descricao: readFirstText(raw, "descricao").trim() || vistoria,
    dataLimite,
    vencido: raw.vencido === true,
    renovaCom: readFirstText(raw, "renovaCom").trim() || null,
  };
}

function mapItems<T>(payload: unknown, mapper: (raw: unknown) => T | null): T[] {
  return extractItems(payload)
    .map(mapper)
    .filter((item): item is T => item !== null);
}

export async function listObraEmbarcacoes({ signal }: RequestOptions = {}): Promise<
  ObraEmbarcacaoOpcao[]
> {
  const response = await getHttpClient().get<unknown>(OBRAS_PATHS.EMBARCACOES, { signal });
  return mapItems(response.data, toEmbarcacaoOpcao);
}

export async function listObraLocais({ signal }: RequestOptions = {}): Promise<ObraLocalOpcao[]> {
  const response = await getHttpClient().get<unknown>(OBRAS_PATHS.LOCAIS, { signal });
  return mapItems(response.data, toLocalOpcao);
}

/** Devolve a tabela inteira; quem consome filtra `disponivelNaCriacao` (RN05). */
export async function listObraStatus({ signal }: RequestOptions = {}): Promise<ObraStatusOpcao[]> {
  const response = await getHttpClient().get<unknown>(OBRAS_PATHS.STATUS_OBRA, { signal });
  return mapItems(response.data, toStatusOpcao);
}

/** Tipos de período na ordem em que a API devolve (já ordenados). */
export async function listObraPeriodos({ signal }: RequestOptions = {}): Promise<
  ObraPeriodoOpcao[]
> {
  const response = await getHttpClient().get<unknown>(OBRAS_PATHS.PERIODOS_OBRA, { signal });
  return mapItems(response.data, toPeriodoOpcao);
}

export async function listObraTiposObra({ signal }: RequestOptions = {}): Promise<TipoObraOpcao[]> {
  const response = await getHttpClient().get<unknown>(OBRAS_PATHS.TIPOS_OBRA, { signal });
  return mapItems(response.data, toTipoObraOpcao);
}

/** Devolve também os bloqueados (`bloqueado`); quem consome não os oferece (RN20). */
export async function listObraCentrosCusto(
  embarcacaoId: string,
  { signal }: RequestOptions = {},
): Promise<CentroCustoOpcao[]> {
  const response = await getHttpClient().get<unknown>(
    OBRAS_PATHS.EMBARCACAO_CENTROS_CUSTO(embarcacaoId),
    { signal },
  );
  return mapItems(response.data, toCentroCustoOpcao);
}

export async function getObraEmbarcacaoInformacoes(
  embarcacaoId: string,
  { signal }: RequestOptions = {},
): Promise<ObraEmbarcacaoInformacoes> {
  const response = await getHttpClient().get<unknown>(
    OBRAS_PATHS.EMBARCACAO_INFORMACOES(embarcacaoId),
    { signal },
  );
  const raw = extractDetail(response.data);
  const record = isRecord(raw) ? raw : {};

  return {
    embarcacaoId: readFirstText(record, "embarcacaoId").trim() || embarcacaoId,
    clienteAtual: readFirstText(record, "clienteAtual").trim(),
    terminoContrato: readFirstText(record, "terminoContrato").trim(),
    tipoEmbarcacao: readFirstText(record, "tipoEmbarcacao").trim(),
    previsaoExtensaoContratual: readFirstText(record, "previsaoExtensaoContratual").trim(),
    ultimaObra: toUltimaObra(record.ultimaObra),
    limitesClasse: mapItems(record.limitesClasse, toLimiteClasse),
  };
}

/** Limites de classe da embarcação, já ordenados pela data limite (HU 2549). */
export async function listObraLimitesClasse(
  embarcacaoId: string,
  { signal }: RequestOptions = {},
): Promise<ObraLimiteClasseDetalhe[]> {
  const response = await getHttpClient().get<unknown>(
    OBRAS_PATHS.EMBARCACAO_LIMITES_CLASSE(embarcacaoId),
    { signal },
  );
  return mapItems(response.data, toLimiteClasseDetalhe);
}

/** HU 2543: grava a obra inteira (única gravação do wizard). Erros: `APIErrorException` (400/403...). */
export async function criarObra(payload: CriarObraPayload): Promise<CriarObraResultado> {
  const response = await getHttpClient().post<unknown>(OBRAS_PATHS.CRIAR, payload);
  const raw = extractDetail(response.data);
  const record = isRecord(raw) ? raw : {};

  return {
    id: readFirstText(record, "id").trim(),
    codigo: readFirstText(record, "codigo").trim(),
  };
}

export const CRIAR_OBRA_ERROR_MESSAGE = "Não foi possível criar a obra.";
export const CRIAR_OBRA_FORBIDDEN_MESSAGE = "Você não tem permissão para criar obras.";

/** Mensagem do core (400/409...) mais as mensagens por campo (`error.details`), sem repetição. */
export function lerErroCriarObra(error: unknown): { mensagem: string; detalhes: string[] } {
  if (!isAPIError(error)) return { mensagem: CRIAR_OBRA_ERROR_MESSAGE, detalhes: [] };
  if (error.status === 403) return { mensagem: CRIAR_OBRA_FORBIDDEN_MESSAGE, detalhes: [] };

  const mensagem = error.message.trim() || CRIAR_OBRA_ERROR_MESSAGE;
  const detalhes = [...new Set(Object.values(readErrorDetails(error.details)))];
  return { mensagem, detalhes: detalhes.filter((detalhe) => detalhe !== mensagem) };
}

export interface ListObrasParams {
  busca?: string;
  filters?: Partial<ObrasFilters>;
  page?: number;
  pageSize?: number;
  sort?: ObrasSort;
  signal?: AbortSignal;
}

function cleanValues(values: string[] | undefined): string[] {
  return (values ?? []).map((value) => value.trim()).filter((value) => value !== "");
}

function buildCriteriaParams(
  busca: string | undefined,
  filters: Partial<ObrasFilters>,
): Record<string, HttpQueryParam> {
  const params: Record<string, HttpQueryParam> = {};

  const term = busca?.trim();
  if (term) params.busca = term;

  const statusObra = cleanValues(filters.statusObra);
  if (statusObra.length > 0) params.statusObra = statusObra;
  const embarcacaoId = cleanValues(filters.embarcacaoId);
  if (embarcacaoId.length > 0) params.embarcacaoId = embarcacaoId;
  const tipoObra = cleanValues(filters.tipoObra);
  if (tipoObra.length > 0) params.tipoObra = tipoObra;
  if (filters.dataInicio) params.dataInicio = filters.dataInicio;
  if (filters.dataFim) params.dataFim = filters.dataFim;

  return params;
}

function readNumber(raw: Record<string, unknown>, key: string): number | null {
  const value = raw[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function toObraListaItem(raw: unknown): ObraListaItem | null {
  if (!isRecord(raw)) return null;

  const id = readFirstText(raw, "id").trim();
  if (!id) return null;

  const embarcacao = isRecord(raw.embarcacao) ? raw.embarcacao : {};
  const status = isRecord(raw.statusObra) ? raw.statusObra : null;
  const statusId = status ? readFirstText(status, "id").trim() : "";
  const condicao = readFirstText(raw, "condicao").trim();

  return {
    id,
    codigo: readFirstText(raw, "codigo").trim() || id,
    embarcacao: {
      id: readFirstText(embarcacao, "id").trim(),
      nome: readFirstText(embarcacao, "nome").trim(),
      acronimo: readFirstText(embarcacao, "acronimo").trim() || null,
    },
    statusObra:
      status && statusId
        ? {
            id: statusId,
            codigo: readFirstText(status, "codigo").trim(),
            descricao: readFirstText(status, "descricao").trim(),
          }
        : null,
    tiposObra: Array.isArray(raw.tiposObra)
      ? raw.tiposObra.filter((tipo): tipo is string => typeof tipo === "string")
      : [],
    inicio: readFirstText(raw, "inicio").trim(),
    fim: readFirstText(raw, "fim").trim(),
    duracaoDias: readNumber(raw, "duracaoDias"),
    condicao: condicao || null,
    realizadoBrlMilhoes: readNumber(raw, "realizadoBrlMilhoes"),
    totalServicos: readNumber(raw, "totalServicos"),
    percentualServicosConcluidos: readNumber(raw, "percentualServicosConcluidos"),
    totalMateriais: readNumber(raw, "totalMateriais"),
    percentualMateriaisEntregues: readNumber(raw, "percentualMateriaisEntregues"),
    totalFacilidades: readNumber(raw, "totalFacilidades"),
    percentualFacilidadesConsumidas: readNumber(raw, "percentualFacilidadesConsumidas"),
    empenhadoPercentual: readNumber(raw, "empenhadoPercentual"),
    comprometidoPercentual: readNumber(raw, "comprometidoPercentual"),
    estimadoPercentual: readNumber(raw, "estimadoPercentual"),
    naoComprometidoPercentual: readNumber(raw, "naoComprometidoPercentual"),
  };
}

function toTotalPorStatus(raw: unknown): ObraTotalPorStatus | null {
  if (!isRecord(raw) || typeof raw.total !== "number") return null;

  return {
    statusObraId: readFirstText(raw, "statusObraId").trim() || null,
    codigo: readFirstText(raw, "codigo").trim() || null,
    total: raw.total,
  };
}

/**
 * HU 2521 — consulta de obras (privilégio `hub.obra:visualizar`). Filtros de lista como chave
 * repetida, `dataInicio`/`dataFim` (interseção de período) e o contrato paginado
 * (`page`, `pageSize`, `sort_by`, `sort_direction`); devolve também o total por status. A tela de cards pede uma coluna
 * por vez (`statusObra` único, `pageSize` 10).
 */
export async function listObras(request: ListObrasParams = {}): Promise<ObrasPage> {
  const {
    busca,
    filters = EMPTY_OBRAS_FILTERS,
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    sort = DEFAULT_OBRAS_SORT,
    signal,
  } = request;

  const params = buildCriteriaParams(busca, filters);

  Object.assign(params, toPaginatedQuery({ page, pageSize, sort }));

  const config: HttpRequestConfig = { signal, params };
  const response = await getHttpClient().get<unknown>(OBRAS_PATHS.LISTAR, config);

  const items = mapItems(response.data, toObraListaItem);
  const detail = extractDetail(response.data);
  const totais = isRecord(detail) ? detail.totaisPorStatus : undefined;

  return {
    items,
    total: extractTotal(detail) ?? items.length,
    totaisPorStatus: Array.isArray(totais)
      ? totais.map(toTotalPorStatus).filter((total): total is ObraTotalPorStatus => total !== null)
      : [],
  };
}

export interface ExportObrasParams {
  busca?: string;
  filters?: Partial<ObrasFilters>;
  /** Rótulos legíveis dos filtros aplicados, para o cabeçalho do arquivo. */
  filtros?: string[];
  sort?: ObrasSort;
  /** Colunas visíveis da tela, na ordem; o gateway as traduz para as chaves do core. */
  colunas: readonly ObrasColumnKey[];
  format: ExportFormat;
  signal?: AbortSignal;
}

/**
 * HU 2531 — exporta a visão atual (mesmos filtros e ordenação de `listObras`, sem paginação,
 * colunas visíveis na ordem da tela). O arquivo é gerado no servidor; o nome vem do
 * `Content-Disposition`.
 */
export async function exportObras({
  busca,
  filters = EMPTY_OBRAS_FILTERS,
  filtros,
  sort = DEFAULT_OBRAS_SORT,
  colunas,
  format,
  signal,
}: ExportObrasParams): Promise<ExportedFile> {
  const params = buildCriteriaParams(busca, filters);

  Object.assign(params, toPaginatedQuery({ sort }));
  params.formato = format;
  appendExportColumns(params, colunas, OBRAS_EXPORT_COLUMN_KEYS, "Obras");
  appendReportQuery(params, filtros);

  const response = await getHttpClient().get<ArrayBuffer>(OBRAS_PATHS.EXPORTAR, {
    signal,
    params,
    responseType: "arraybuffer",
  });

  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}
